import { useState, useCallback } from 'react';

const STORAGE_KEY = 'sg_scraper_user_config';

function loadConfig() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveConfig(config) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch {
    // localStorage full or unavailable
  }
}

/**
 * Hook to persist per-page user settings in localStorage.
 *
 * Usage:
 *   const [pageSize, setPageSize] = useUserConfig('sgPools.pageSize', 50);
 *   const [startDate, setStartDate] = useUserConfig('sgPools.startDate', today);
 *
 * Key format: "pageName.settingName"
 * Value is JSON-serializable (string, number, array, object).
 */
export function useUserConfig(key, defaultValue) {
  const [value, setValue] = useState(() => {
    const config = loadConfig();
    return config[key] !== undefined ? config[key] : defaultValue;
  });

  const set = useCallback((newValue) => {
    // Support functional updates like setState((prev) => ...)
    setValue((prev) => {
      const resolved = typeof newValue === 'function' ? newValue(prev) : newValue;
      const config = loadConfig();
      config[key] = resolved;
      saveConfig(config);
      return resolved;
    });
  }, [key]);

  return [value, set];
}
